import React, { FC, PropsWithChildren, ReactNode } from "react"
import { useErrorBoundary } from "use-error-boundary"
import { InfoBox } from "@bancacfplus/InfoBox"
import { AxiosError } from "axios"
import { ErrorLike } from "@apollo/client"
import style from "./ErrorBoundaryEnriched.module.scss"
import { showReportDialog, useSentry } from "../Sentry/SentryHook"
import { getCustomErrorComponent } from "./CustomErrorResolver"

type ErrorBoundaryEnrichedProps = PropsWithChildren<{
  error?: Error | AxiosError | ErrorLike
  swallow?: boolean
  message?: ReactNode
  errorContainer?: boolean
  className?: string
}>

export const ErrorBoundaryEnriched: FC<ErrorBoundaryEnrichedProps> = ({
  error,
  swallow = false,
  message = "Ops… qualcosa è andato storto.",
  errorContainer = false,
  children,
  className
}) => {
  const { captureMessage } = useSentry()

  const {
    ErrorBoundary,
    didCatch,
    error: boundaryError
  } = useErrorBoundary({
    onDidCatch: captureMessage
  })

  if (didCatch || error) {
    if (error) {
      captureMessage(error.message)
    }

    if (boundaryError) {
      captureMessage(boundaryError.message)
    }

    if (swallow) {
      return <></>
    }

    // Sometimes it may be necessary to handle a specific error instead of raising the common error message
    // Checking if error matches known cases for custom handling otherwise falling back to common error display
    const CustomError = boundaryError ? getCustomErrorComponent(boundaryError) : getCustomErrorComponent(error)
    if (CustomError) return <CustomError />

    return (
      <div className={`${errorContainer ? style.errorContainer : ""} ${className ?? ""}`}>
        <InfoBox type="error">
          {message}
          <InfoBox.Action onClick={showReportDialog}>Segnalaci l&apos;errore</InfoBox.Action>
        </InfoBox>
      </div>
    )
  }

  return <ErrorBoundary>{children}</ErrorBoundary>
}
